import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CssBaseline, Toolbar } from "@mui/material";
import { jwtDecode } from 'jwt-decode';

import AboutApi from "../../../apis/authorization/login-api";
import CompanyApi from "../../../apis/about/about-api";
import InstallationApi from "../../../apis/installation/installation";
import { activeInstallation, logoutUser, updateToken, updateActiveUser, userSwitch } from "../../../slice/UserSlice";
import { saveReturnUrl } from "../../../helper/return-url-helper";
import { buildCompanyPath, parseContextFromPath } from "../../../helper/url-builder";

import MenuSidebar from "../layout-component/sidebar/menu-sidebar";
import MainFooter from "../layout-component/main-footer";
import Admin from "../../admin";
import UserIdleTimer from "../idle-timer/user-idle-timer";

function AdminRoutes() {
    return <Admin />;
}

function resolveCompanyTypeCode(companyType) {
    if (!companyType) return 'R';
    if (typeof companyType === 'object') {
        return companyType.companyId || companyType.companyType || 'R';
    }
    return String(companyType);
}

function normalizeInstallationForStore(installation, urlInstallationId) {
    if (!installation) {
        return {
            id: urlInstallationId,
            installationId: urlInstallationId,
        };
    }
    const id = installation.id ?? installation.installationId ?? urlInstallationId;
    return {
        ...installation,
        id: String(id),
        installationId: String(id),
        installationCode: installation.installationCode ?? installation.installation_code ?? '',
    };
}

async function fetchInstallationForRestore(companyId, installationId, token) {
    const res = await InstallationApi.fetchInstallationData({ companyId }, token);
    const installations = res?.data?.installations || [];
    return installations.find(
        (item) => String(item.id ?? item.installationId) === String(installationId)
    ) || installations[0] || null;
}

function buildActiveCompanyFromApi(company, urlCompanyId, urlInstallationId, installation = null) {
    const user = {
        companyId: String(company.companyId ?? urlCompanyId),
        companyName: company.companyName || '',
        companyType: resolveCompanyTypeCode(company.companyType),
        timezone: company.timezone,
        managedBy: company.managedBy,
        city: company.city || '',
        region: company.region || '',
        country: company.country || '',
        installtionExist: !!urlInstallationId,
    };
    if (!urlInstallationId) return user;
    const normalized = normalizeInstallationForStore(installation, urlInstallationId);
    return {
        ...user,
        installation: normalized,
        installationId: String(normalized.id),
    };
}

function CompanyDataLoader({ authUser }) {
    const dispatch = useDispatch();
    const location = useLocation();

    useEffect(() => {
        const { companyId: urlCompanyId, installationId: urlInstallationId } = parseContextFromPath(location.pathname || '');
        if (!urlCompanyId || !authUser.token) return;

        const activeUser = authUser.users?.[0];
        const activeCompanyId = activeUser?.companyId?.toString() || '';
        const activeInstallationId =
            activeUser?.installation?.id?.toString() ||
            activeUser?.installationId?.toString() ||
            '';

        const companyMismatch = activeCompanyId !== urlCompanyId;
        const installationMismatch = !!urlInstallationId && activeInstallationId !== urlInstallationId;

        if (!companyMismatch && !installationMismatch) {
            if (activeUser?.timezone) return;
            CompanyApi.fetchCompanyData(urlCompanyId, {}, authUser.token)
                .then((res) => {
                    const raw = res?.data ?? res;
                    const company = raw?.companies?.[0] ?? raw;
                    if (company?.timezone) {
                        dispatch(updateActiveUser({ timezone: company.timezone }));
                    }
                })
                .catch(() => {});
            return;
        }

        const syncContext = async () => {
            const companyRes = await CompanyApi.fetchCompanyData(urlCompanyId, {}, authUser.token);
            const raw = companyRes?.data ?? companyRes;
            const company = raw?.companies?.[0] ?? raw;
            if (!company) return;

            let installation = null;
            if (urlInstallationId) {
                try {
                    installation = await fetchInstallationForRestore(urlCompanyId, urlInstallationId, authUser.token);
                } catch {
                    installation = null;
                }
            }

            if (companyMismatch) {
                dispatch(userSwitch([[
                    buildActiveCompanyFromApi(company, urlCompanyId, urlInstallationId, installation),
                ]]));
                return;
            }

            if (installationMismatch && urlInstallationId) {
                dispatch(activeInstallation({
                    installation: normalizeInstallationForStore(installation, urlInstallationId),
                }));
            }

            if (company.timezone) {
                dispatch(updateActiveUser({ timezone: company.timezone }));
            }
        };

        syncContext().catch(() => {});
    }, [location.pathname, authUser.token, authUser.users, dispatch]);

    return null;
}

function SecurePath({ authUser }) {
    const dispatch = useDispatch();

    useEffect(() => {
        let refreshTimeoutId = null;

        const scheduleTokenRefresh = () => {
            if (!authUser.token || !authUser.refreshToken) return;

            try {
                const decoded = jwtDecode(authUser.token);
                const expiryTime = decoded.exp * 1000; // Convert to milliseconds
                const currentTime = Date.now();
                // Refresh 60 seconds before expiry, minimum 10 seconds from now
                const refreshIn = Math.max(expiryTime - currentTime - 60000, 10000);

                // Debug: Token refresh scheduled (use browser devtools to see timing)

                refreshTimeoutId = setTimeout(() => {
                    AboutApi.getAccessToken({ refreshToken: authUser.refreshToken }, null)
                        .then((res) => {
                            dispatch(updateToken({
                                token: res.data.token,
                                refreshToken: res.data.refreshToken || authUser.refreshToken,
                            }));
                        })
                        .catch((error) => {
                            console.error('Token refresh failed:', error);
                            dispatch(logoutUser());
                        });
                }, refreshIn);
            } catch (error) {
                console.error('Error decoding token for refresh scheduling:', error);
                // Fall back to attempting refresh now
                AboutApi.getAccessToken({ refreshToken: authUser.refreshToken }, null)
                    .then((res) => dispatch(updateToken({
                        token: res.data.token,
                        refreshToken: res.data.refreshToken || authUser.refreshToken,
                    })))
                    .catch(() => dispatch(logoutUser()));
            }
        };

        scheduleTokenRefresh();

        return () => {
            if (refreshTimeoutId) clearTimeout(refreshTimeoutId);
        };
    }, [authUser.token, authUser.refreshToken, dispatch]);

    return (
        <>
            <CompanyDataLoader authUser={authUser} />
            <UserIdleTimer />
            <CssBaseline />
            <Box sx={{ display: 'flex', background: "#F5F6FA" }}>
                <MenuSidebar />
                <Box component="main" sx={{ flexGrow: 1, p: 0, minHeight: "100%", overflow: "hidden" }}>
                    <Toolbar sx={{ height: "55px !important", minHeight: "55px !important", maxHeight: "55px !important" }} />
                    <Box sx={{ height: "calc(100vh - 55px)", display: "flex", flexFlow: "column", overflow: "auto" }}>
                        <Box sx={{ flex: "1 1", overflow: "hidden" }}>
                            <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
                                <AdminRoutes />
                            </Box>
                        </Box>
                        <Box sx={{ flex: "fit-content 0" }}>
                            <MainFooter />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

function AuthenticatedRouteRedirect() {
    const location = useLocation();
    const authUser = useSelector((state) => state.authUser);
    const companyId = authUser.users[0]?.companyId?.toString() || "";

    // Redirect /app or / to the user's primary company about page
    if (location.pathname === "/app" || location.pathname === "/") {
        if (companyId) {
            return <Navigate to={buildCompanyPath.about(companyId)} />;
        }
        return <Navigate to="/login" />;
    }

    return <Outlet />;
}

function ProtectedRoutes() {
    const isAuthenticated = useSelector((state) => state.authUser.isAuthenticated);
    const location = useLocation();

    if (!isAuthenticated) {
        saveReturnUrl(location.pathname + location.search);
        return <Navigate to="/login" />;
    }

    return <AuthenticatedRouteRedirect />;
}

function PrivateRoutes() {
    const authUser = useSelector((state) => state.authUser);

    return (
        <Routes>
            <Route path="/" element={<ProtectedRoutes />}>
                <Route path="/app/*" element={<SecurePath authUser={authUser} />} />
                <Route path="*" element={<>Not Found</>} />
            </Route>
        </Routes>
    );
}

export default PrivateRoutes;
